import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16 xl:px-40 py-12">
      <h1 className="text-4xl font-bold text-primary mb-8">Privacy Policy</h1>

      <p className="text-gray-700 mb-6">
        <strong>Last Updated:</strong> November 30, 2023
      </p>

      <p className="text-gray-700 mb-6">
        This Privacy Policy describes Our policies and procedures on the
        collection, use, and disclosure of Your information when You use the
        Service and tells You about Your privacy rights and how the law protects
        You.
      </p>

      <p className="text-gray-700 mb-6">
        We use Your Personal data to provide and improve the Service. By using
        the Service, You agree to the collection and use of information in
        accordance with this Privacy Policy.
      </p>

      {/* Interpretation & Definitions */}
      <h2 className="text-2xl font-bold text-secondary mt-8 mb-4">
        1. Interpretation and Definitions
      </h2>

      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Interpretation
      </h3>
      <p className="text-gray-700 mb-6">
        The words of which the initial letter is capitalized have meanings
        defined under the following conditions. The following definitions shall
        have the same meaning regardless of whether they appear in singular or
        plural.
      </p>

      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Definitions
      </h3>
      <ul className="list-disc list-inside text-gray-700 mb-6">
        <li>
          <strong>Account:</strong> A unique account created for You to access
          our Service or parts of our Service.
        </li>
        <li>
          <strong>Affiliate:</strong> An entity that controls, is controlled by,
          or is under common control with a party, where "control" means
          ownership of 50% or more of the shares, equity interest, or other
          securities entitled to vote for election of directors or other
          managing authority.
        </li>
        <li>
          <strong>Company:</strong> (referred to as either "the Company", "We",
          "Us" or "Our" in this Agreement) refers to Surface Pix LLC, 1587 S
          Terrace Road, Saratoga Springs, Utah 84045.
        </li>
        <li>
          <strong>Cookies:</strong> Small files that are placed on Your
          computer, mobile device, or any other device by a website, containing
          the details of Your browsing history on that website among its many
          uses.
        </li>
        <li>
          <strong>Country:</strong> Refers to Utah, United States.
        </li>
        <li>
          <strong>Device:</strong> Any device that can access the Service, such
          as a computer, a cellphone, or a digital tablet.
        </li>
        <li>
          <strong>Personal Data:</strong> Any information that relates to an
          identified or identifiable individual.
        </li>
        <li>
          <strong>Service:</strong> Refers to the Website (
          <a
            href="https://www.surfacepix.com"
            className="text-secondary hover:underline"
          >
            https://www.surfacepix.com
          </a>
          ).
        </li>
        <li>
          <strong>Service Provider:</strong> Any natural or legal person who
          processes data on behalf of the Company. It refers to third-party
          companies or individuals employed by the Company to facilitate the
          Service, provide the Service on behalf of the Company, perform
          services related to the Service, or assist the Company in analyzing
          how the Service is used.
        </li>
        <li>
          <strong>Usage Data:</strong> Data collected automatically, either
          generated by the use of the Service or from the Service infrastructure
          itself (e.g., the duration of a page visit).
        </li>
        <li>
          <strong>Website:</strong> Refers to SurfacePix, accessible from{" "}
          <a
            href="https://www.surfacepix.com"
            className="text-secondary hover:underline"
          >
            https://www.surfacepix.com
          </a>
          .
        </li>
        <li>
          <strong>You:</strong> The individual accessing or using the Service,
          or the company or other legal entity on behalf of which such
          individual is accessing or using the Service, as applicable.
        </li>
      </ul>

      {/* Collecting and Using Your Personal Data */}
      <h2 className="text-2xl font-bold text-secondary mt-8 mb-4">
        2. Collecting and Using Your Personal Data
      </h2>

      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Types of Data Collected
      </h3>

      <h4 className="text-lg font-semibold text-primary mt-4 mb-2">
        Personal Data
      </h4>
      <p className="text-gray-700 mb-6">
        While using Our Service, We may ask You to provide Us with certain
        personally identifiable information that can be used to contact or
        identify You. Personally identifiable information may include, but is
        not limited to:
      </p>
      <ul className="list-disc list-inside text-gray-700 mb-6">
        <li>Email address</li>
        <li>First name and last name</li>
        <li>Phone number</li>
      </ul>

      <h4 className="text-lg font-semibold text-primary mt-4 mb-2">
        Usage Data
      </h4>
      <p className="text-gray-700 mb-6">
        Usage Data is collected automatically when using the Service.
      </p>
      <p className="text-gray-700 mb-6">
        Usage Data may include information such as Your Device's Internet
        Protocol address (e.g., IP address), browser type, browser version, the
        pages of our Service that You visit, the time and date of Your visit,
        the time spent on those pages, unique device identifiers, and other
        diagnostic data.
      </p>
      <p className="text-gray-700 mb-6">
        When You access the Service by or through a mobile device, We may
        collect certain information automatically, including, but not limited
        to, the type of mobile device You use, Your mobile device unique ID, the
        IP address of Your mobile device, Your mobile operating system, the type
        of mobile Internet browser You use, unique device identifiers, and other
        diagnostic data.
      </p>
      <p className="text-gray-700 mb-6">
        We may also collect information that Your browser sends whenever You
        visit our Service or when You access the Service by or through a mobile
        device.
      </p>

      {/* Tracking Technologies & Cookies */}
      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Tracking Technologies and Cookies
      </h3>
      <p className="text-gray-700 mb-6">
        We use Cookies and similar tracking technologies to track the activity
        on Our Service and store certain information. Tracking technologies used
        are beacons, tags, and scripts to collect and track information and to
        improve and analyze Our Service. The technologies We use may include:
      </p>

      <h4 className="text-lg font-semibold text-primary mt-4 mb-2">
        Cookies or Browser Cookies
      </h4>
      <p className="text-gray-700 mb-6">
        A cookie is a small file placed on Your Device. You can instruct Your
        browser to refuse all Cookies or to indicate when a Cookie is being
        sent. However, if You do not accept Cookies, You may not be able to use
        some parts of our Service. Unless you have adjusted Your browser setting
        so that it will refuse Cookies, our Service may use Cookies.
      </p>

      <h4 className="text-lg font-semibold text-primary mt-4 mb-2">
        Web Beacons
      </h4>
      <p className="text-gray-700 mb-6">
        Certain sections of our Service and our emails may contain small
        electronic files known as web beacons (also referred to as clear gifs,
        pixel tags, and single-pixel gifs) that permit the Company, for example,
        to count users who have visited those pages or opened an email and for
        other related website statistics (e.g., recording the popularity of a
        certain section and verifying system and server integrity).
      </p>

      <h4 className="text-lg font-semibold text-primary mt-4 mb-2">
        Types of Cookies
      </h4>
      <p className="text-gray-700 mb-6">
        Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies
        remain on Your personal computer or mobile device when You go offline,
        while Session Cookies are deleted as soon as You close Your web browser.
      </p>

      <h4 className="text-lg font-semibold text-primary mt-4 mb-2">
        We use both Session and Persistent Cookies for the purposes set out
        below:
      </h4>

      <ul className="list-disc list-inside text-gray-700 mb-6">
        <li>
          <strong>Necessary / Essential Cookies:</strong>
          <br />
          <span className="italic">Type:</span> Session Cookies
          <br />
          <span className="italic">Administered by:</span> Us
          <br />
          <span className="italic">Purpose:</span> These Cookies are essential
          to provide You with services available through the Website and to
          enable You to use some of its features. They help to authenticate
          users and prevent fraudulent use of user accounts. Without these
          Cookies, the services that You have asked for cannot be provided, and
          We only use these Cookies to provide You with those services.
        </li>
        <li className="mt-4">
          <strong>Cookies Policy / Notice Acceptance Cookies:</strong>
          <br />
          <span className="italic">Type:</span> Persistent Cookies
          <br />
          <span className="italic">Administered by:</span> Us
          <br />
          <span className="italic">Purpose:</span> These Cookies identify if
          users have accepted the use of cookies on the Website.
        </li>
        <li className="mt-4">
          <strong>Functionality Cookies:</strong>
          <br />
          <span className="italic">Type:</span> Persistent Cookies
          <br />
          <span className="italic">Administered by:</span> Us
          <br />
          <span className="italic">Purpose:</span> These Cookies allow us to
          remember choices You make when You use the Website, such as
          remembering your login details or language preference. The purpose of
          these Cookies is to provide You with a more personal experience and to
          avoid You having to re-enter your preferences every time You use the
          Website.
        </li>
      </ul>

      <p className="text-gray-700">
        For more information about the cookies we use and your choices regarding
        cookies, please visit our Cookies Policy or the Cookies section of our
        Privacy Policy.
      </p>

      {/* Use of Your Personal Data */}
      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Use of Your Personal Data
      </h3>
      <p className="text-gray-700 mb-6">
        The Company may use Personal Data for the following purposes:
      </p>
      <ul className="list-disc list-inside text-gray-700 mb-6">
        <li>
          To provide and maintain our Service, including monitoring usage.
        </li>
        <li>
          To manage Your Account: Your registration and user access to Service
          functionalities.
        </li>
        <li>
          For the performance of a contract: Fulfilling purchase agreements or
          other contracts.
        </li>
        <li>
          To contact You via email, phone, SMS, or push notifications regarding
          service updates.
        </li>
        <li>
          To provide You with news, special offers, and relevant information
          unless You opt-out.
        </li>
        <li>To manage and respond to Your requests.</li>
        <li>
          For business transfers: If the Company undergoes a merger,
          restructuring, or sale, Your data may be transferred.
        </li>
        <li>
          For other purposes: Data analysis, improving our services, and
          evaluating marketing efforts.
        </li>
      </ul>

      {/* Retention of Your Personal Data */}
      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Retention of Your Personal Data
      </h3>
      <p className="text-gray-700 mb-6">
        The Company will retain Your Personal Data only as long as necessary for
        the purposes outlined in this Privacy Policy. We will also retain and
        use Your Personal Data to comply with legal obligations, resolve
        disputes, and enforce policies.
      </p>
      <p className="text-gray-700 mb-6">
        Usage Data is generally retained for a shorter period, except when
        needed to enhance security, improve functionality, or comply with legal
        requirements.
      </p>

      {/* Transfer of Your Personal Data */}
      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Transfer of Your Personal Data
      </h3>
      <p className="text-gray-700 mb-6">
        Your information may be transferred to and maintained on computers
        outside of Your state, province, or country where data protection laws
        may differ. Your consent to this Privacy Policy followed by Your
        submission of such information represents Your agreement to that
        transfer.
      </p>
      <p className="text-gray-700 mb-6">
        The Company ensures that all necessary measures are in place to protect
        Your data and that no transfer occurs unless proper security controls
        are implemented.
      </p>

      {/* Delete Your Personal Data */}
      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Delete Your Personal Data
      </h3>
      <p className="text-gray-700 mb-6">
        You have the right to request deletion of Your Personal Data. You can
        manage Your information through Your account settings or contact Us
        directly.
      </p>
      <p className="text-gray-700 mb-6">
        However, certain information may be retained due to legal obligations.
      </p>

      {/* Disclosure of Your Personal Data */}
      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Disclosure of Your Personal Data
      </h3>

      <h4 className="text-lg font-semibold text-primary mt-4 mb-2">
        Business Transactions
      </h4>
      <p className="text-gray-700 mb-6">
        If the Company is involved in a merger, acquisition, or asset sale, Your
        Personal Data may be transferred as part of that transaction. Notice
        will be provided before any such transfer.
      </p>

      <h4 className="text-lg font-semibold text-primary mt-4 mb-2">
        Law Enforcement
      </h4>
      <p className="text-gray-700 mb-6">
        Under certain circumstances, the Company may be required to disclose
        Your Personal Data by law or in response to valid legal requests by
        public authorities (e.g., court or government agencies).
      </p>

      <h4 className="text-lg font-semibold text-primary mt-4 mb-2">
        Other Legal Requirements
      </h4>
      <p className="text-gray-700 mb-6">
        The Company may disclose Your Personal Data if such action is necessary
        to:
      </p>
      <ul className="list-disc list-inside text-gray-700 mb-6">
        <li>Comply with legal obligations.</li>
        <li>Protect and defend the rights or property of the Company.</li>
        <li>
          Investigate potential wrongdoing in connection with the Service.
        </li>
        <li>Protect the personal safety of Users or the public.</li>
        <li>Protect against legal liability.</li>
      </ul>

      {/* Security of Your Personal Data */}
      <h3 className="text-xl font-semibold text-primary mt-6 mb-2">
        Security of Your Personal Data
      </h3>
      <p className="text-gray-700 mb-6">
        The security of Your Personal Data is important to Us, but remember that
        no method of transmission over the Internet or method of electronic
        storage is 100% secure. While We strive to use commercially acceptable
        means to protect Your Personal Data, We cannot guarantee its absolute
        security.
      </p>

      {/* Children's Privacy */}
      <h2 className="text-2xl font-bold text-secondary mt-8 mb-4">
        3. Children's Privacy
      </h2>
      <p className="text-gray-700 mb-6">
        Our Service does not address anyone under the age of 13. We do not
        knowingly collect personally identifiable information from anyone under
        the age of 13. If You are a parent or guardian and You are aware that
        Your child has provided Us with Personal Data, please contact Us. If We
        become aware that We have collected Personal Data from anyone under the
        age of 13 without verification of parental consent, We take steps to
        remove that information from Our servers.
      </p>
      <p className="text-gray-700 mb-6">
        If We need to rely on consent as a legal basis for processing Your
        information and Your country requires consent from a parent, We may
        require Your parent's consent before We collect and use that
        information.
      </p>

      {/* Links to Other Websites */}
      <h2 className="text-2xl font-bold text-secondary mt-8 mb-4">
        4. Links to Other Websites
      </h2>
      <p className="text-gray-700 mb-6">
        Our Service may contain links to other websites that are not operated by
        Us. If You click on a third-party link, You will be directed to that
        third party's site. We strongly advise You to review the Privacy Policy
        of every site You visit.
      </p>
      <p className="text-gray-700 mb-6">
        We have no control over and assume no responsibility for the content,
        privacy policies, or practices of any third-party sites or services.
      </p>

      {/* Changes to This Privacy Policy */}
      <h2 className="text-2xl font-bold text-secondary mt-8 mb-4">
        5. Changes to This Privacy Policy
      </h2>
      <p className="text-gray-700 mb-6">
        We may update Our Privacy Policy from time to time. We will notify You
        of any changes by posting the new Privacy Policy on this page.
      </p>
      <p className="text-gray-700 mb-6">
        We will let You know via email and/or a prominent notice on Our Service,
        prior to the change becoming effective and update the "Last Updated"
        date at the top of this Privacy Policy.
      </p>
      <p className="text-gray-700 mb-6">
        You are advised to review this Privacy Policy periodically for any
        changes. Changes to this Privacy Policy are effective when they are
        posted on this page.
      </p>

      {/* Contact Us */}
      <h2 className="text-2xl font-bold text-secondary mt-8 mb-4">
        6. Contact Us
      </h2>
      <p className="text-gray-700 mb-6">
        If you have any questions about this Privacy Policy, You can contact us:
      </p>
      <ul className="list-none text-gray-700 mb-6">
        <li>
          By visiting this page on our website:{" "}
          <a
            href="https://www.surfacepix.com"
            className="text-secondary hover:underline"
          >
            https://www.surfacepix.com
          </a>
        </li>
        <li>
          Email:{" "}
          <a
            href="mailto:info@surfacepix.com"
            className="text-secondary hover:underline"
          >
            info@surfacepix.com
          </a>
        </li>
        <li>Phone: +1 385.625.9812</li>
        <li>Address: 1587 S Terrace Road, Saratoga Springs, UT 84045</li>
      </ul>
    </div>
  );
};

export default PrivacyPolicy;
